/**
 * Session state: lifecycle, live events, and the derived view the shell renders.
 *
 * All server-pushed mutations funnel through one reducer so that ordering is
 * explicit. Frames in particular arrive out of order and can arrive before the
 * checkpoint that owns them, which the old imperative version handled by
 * refetching the whole checkpoint list on every frame.
 */
import { useCallback, useReducer } from 'react';

import type {
  ChatMessage,
  Checkpoint,
  Frame,
  NpcLineCue,
  ServerEvent,
  Session,
  TranscriptTurn,
} from '@/api/types';
import { turnKey } from '@/components/TranscriptPane';

export interface LogLine {
  id: number;
  text: string;
  level: 'info' | 'ok' | 'warn';
}

interface State {
  session: Session | null;
  checkpoints: Checkpoint[];
  transcript: TranscriptTurn[];
  seenTurns: Set<string>;
  chat: ChatMessage[];
  npcLines: NpcLineCue[];
  narration: string;
  log: LogLine[];
  status: string;
}

type Action =
  | { type: 'session/set'; session: Session | null }
  | { type: 'session/suspended'; reason: string }
  | { type: 'checkpoints/set'; checkpoints: Checkpoint[] }
  | { type: 'transcript/append'; turn: TranscriptTurn }
  | { type: 'frame/upsert'; frame: Frame }
  | { type: 'chat/set'; messages: ChatMessage[] }
  | { type: 'chat/upsert'; message: ChatMessage }
  | { type: 'npc-lines/set'; cues: NpcLineCue[] }
  | { type: 'npc-lines/upsert'; cue: NpcLineCue }
  | { type: 'npc-lines/remove'; cueId: string }
  | { type: 'narration/set'; text: string }
  | { type: 'status/set'; text: string }
  | { type: 'log/append'; text: string; level: LogLine['level'] }
  | { type: 'reset' };

const MAX_LOG_LINES = 200;

const initialState: State = {
  session: null,
  checkpoints: [],
  transcript: [],
  seenTurns: new Set(),
  chat: [],
  npcLines: [],
  narration: '',
  log: [],
  status: '',
};

let logSeq = 0;

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'session/set':
      return {
        ...state,
        session: action.session,
        // /sessions/active joins the already-posted opening narration. Use it
        // immediately on reload instead of waiting for the event stream to
        // replay the same durable event a moment later.
        narration: action.session?.opening_narration ?? state.narration,
      };

    case 'session/suspended':
      // No session to update means the event arrived for one we are not
      // showing; dropping it is right, and inventing a session row is not.
      if (!state.session) return state;
      return {
        ...state,
        session: {
          ...state.session,
          status: 'suspended',
          suspended_reason: action.reason,
        },
      };

    case 'checkpoints/set':
      // Newest first — during play only the latest checkpoint is actionable.
      return {
        ...state,
        checkpoints: [...action.checkpoints].sort((a, b) =>
          b.created_at.localeCompare(a.created_at),
        ),
      };

    case 'transcript/append': {
      // The same turn can arrive twice: once over the audio socket as the
      // provider finalizes it, and again over SSE from the database.
      const key = turnKey(action.turn, state.transcript.length);
      if (state.seenTurns.has(key)) return state;
      const seenTurns = new Set(state.seenTurns);
      seenTurns.add(key);
      return {
        ...state,
        seenTurns,
        transcript: [...state.transcript, action.turn],
      };
    }

    case 'frame/upsert': {
      const { frame } = action;
      let touched = false;
      const checkpoints = state.checkpoints.map((checkpoint) => {
        if (checkpoint.id !== frame.checkpoint_id) return checkpoint;
        touched = true;
        const frames = checkpoint.frames ?? [];
        const at = frames.findIndex((f) => f.id === frame.id);
        return {
          ...checkpoint,
          // Merge rather than replace: the event carries the freshly rendered
          // url and status, but not every column the list endpoint returns.
          frames:
            at === -1
              ? [...frames, frame]
              : frames.map((f, i) => (i === at ? { ...f, ...frame } : f)),
        };
      });
      // A frame for a checkpoint we haven't fetched yet is dropped here; the
      // checkpoint.created handler refetches and picks it up.
      return touched ? { ...state, checkpoints } : state;
    }

    case 'chat/set':
      return { ...state, chat: action.messages };

    case 'chat/upsert': {
      // The agent's reply arrives over SSE after the GM's message is already
      // on screen, so this merges by id rather than appending blindly.
      const at = state.chat.findIndex((m) => m.id === action.message.id);
      return {
        ...state,
        chat:
          at === -1
            ? [...state.chat, action.message]
            : state.chat.map((m, i) => (i === at ? { ...m, ...action.message } : m)),
      };
    }

    case 'npc-lines/set':
      return { ...state, npcLines: action.cues };

    case 'npc-lines/upsert': {
      const at = state.npcLines.findIndex((cue) => cue.id === action.cue.id);
      return {
        ...state,
        npcLines:
          at === -1
            ? [...state.npcLines, action.cue]
            : state.npcLines.map((cue, index) =>
                index === at ? { ...cue, ...action.cue } : cue,
              ),
      };
    }

    case 'npc-lines/remove':
      return {
        ...state,
        npcLines: state.npcLines.filter((cue) => cue.id !== action.cueId),
      };

    case 'narration/set':
      return { ...state, narration: action.text };

    case 'status/set':
      return { ...state, status: action.text };

    case 'log/append': {
      logSeq += 1;
      const line: LogLine = { id: logSeq, text: action.text, level: action.level };
      return { ...state, log: [...state.log, line].slice(-MAX_LOG_LINES) };
    }

    case 'reset':
      return { ...initialState, seenTurns: new Set(), log: state.log };

    default:
      return state;
  }
}

export function useSessionState() {
  const [state, dispatch] = useReducer(reducer, initialState);

  const log = useCallback(
    (text: string, level: LogLine['level'] = 'info') =>
      dispatch({ type: 'log/append', text, level }),
    [],
  );

  /** Apply one server event. Returns true when checkpoints need refetching. */
  const applyEvent = useCallback((event: ServerEvent): boolean => {
    switch (event.kind) {
      case 'transcript.appended':
        dispatch({ type: 'transcript/append', turn: event.data as TranscriptTurn });
        return false;

      case 'frame.ready':
        dispatch({ type: 'frame/upsert', frame: event.data as Frame });
        return false;

      case 'frame.failed': {
        // Subscribed but unhandled until now, so a frame that failed to render
        // sat at "Frames queued" for the rest of the session — and the
        // checkpoint had already finalized, so nothing was coming to replace
        // it. Storyboard has had the copy for this state the whole time; it
        // just never got the row.
        const frame = event.data as Frame & { reason?: string };
        dispatch({ type: 'frame/upsert', frame });
        dispatch({
          type: 'log/append',
          text:
            frame.reason === 'budget'
              ? 'a frame was not rendered — the session spend cap was reached'
              : 'a frame failed to render',
          level: 'warn',
        });
        return false;
      }

      case 'chat.message':
        dispatch({ type: 'chat/upsert', message: event.data as ChatMessage });
        return false;

      case 'npc_line.surfaced':
        dispatch({ type: 'npc-lines/upsert', cue: event.data as NpcLineCue });
        return false;

      case 'npc_line.resolved':
        dispatch({
          type: 'npc-lines/remove',
          cueId: (event.data as NpcLineCue).id,
        });
        return false;

      case 'narration.posted': {
        const { text } = event.data as { text: string };
        dispatch({ type: 'narration/set', text });
        dispatch({ type: 'log/append', text: 'opening narration posted', level: 'ok' });
        return false;
      }

      case 'session.suspended': {
        const { reason } = event.data as { reason: string };
        // The status has to change, not just the log.
        //
        // This used to append one warn line to a scrolling panel and leave
        // `session.status` on 'live'. Everything downstream is derived from
        // that field: the mic kept capturing and transcribing — real money to
        // the transcription provider — for a session the agent had stopped
        // ticking, the recap never appeared, and the GM went on playing to a
        // tool that had quietly stopped listening. A spend cap that fires
        // silently is the same as no spend cap for anyone at the table.
        dispatch({ type: 'session/suspended', reason });
        dispatch({
          type: 'log/append',
          text: `session suspended: ${reason}`,
          level: 'warn',
        });
        return false;
      }

      case 'checkpoint.created':
      case 'checkpoint.rejected':
        return true;

      default:
        return false;
    }
  }, []);

  return { state, dispatch, log, applyEvent };
}
